/* eslint-disable react-refresh/only-export-components */
import { useAuth0 } from "@auth0/auth0-react";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import {
  AUTH0_ROLES_CLAIM,
  COSMOS_ACCESS_ROLE,
  hasCosmosAccess,
  parseAihrRoles
} from "@/lib/auth/aihr-roles";

export interface AihrUser {
  name?: string;
  email?: string;
  picture?: string;
  roles: string[];
}

interface AihrAuthContextValue {
  authEnabled: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  error?: Error;
  user?: AihrUser;
  hasCosmosAccess: boolean;
  login: () => Promise<unknown>;
  logout: () => void;
  getAccessToken: () => Promise<string | undefined>;
}

const localUser: AihrUser = {
  name: "Local Cosmos User",
  email: "local@aihr.com",
  roles: [COSMOS_ACCESS_ROLE]
};

const LOCAL_AUTH_SIGNED_OUT_KEY = "cosmos:local-auth:signed-out";
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE as string | undefined;
const AUTH0_SCOPE = "openid profile email offline_access";

const getLocalAuthSignedIn = (): boolean => {
  if (typeof window === "undefined") {
    return true;
  }

  return window.localStorage.getItem(LOCAL_AUTH_SIGNED_OUT_KEY) !== "true";
};

const localAuth: AihrAuthContextValue = {
  authEnabled: false,
  isLoading: false,
  isAuthenticated: true,
  error: undefined,
  user: localUser,
  hasCosmosAccess: true,
  login: async () => undefined,
  logout: () => undefined,
  getAccessToken: async () => undefined
};

const AihrAuthContext = createContext<AihrAuthContextValue>(localAuth);

export function LocalAuthProvider({ children }: PropsWithChildren) {
  const [isAuthenticated, setIsAuthenticated] = useState(getLocalAuthSignedIn);

  const login = useCallback(async () => {
    window.localStorage.removeItem(LOCAL_AUTH_SIGNED_OUT_KEY);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    window.localStorage.setItem(LOCAL_AUTH_SIGNED_OUT_KEY, "true");
    setIsAuthenticated(false);
  }, []);

  const value = useMemo<AihrAuthContextValue>(
    () => ({
      ...localAuth,
      isAuthenticated,
      user: isAuthenticated ? localUser : undefined,
      hasCosmosAccess: isAuthenticated,
      login,
      logout
    }),
    [isAuthenticated, login, logout]
  );

  return (
    <AihrAuthContext.Provider value={value}>
      {children}
    </AihrAuthContext.Provider>
  );
}

export function Auth0Bridge({ children }: PropsWithChildren) {
  const auth0 = useAuth0();
  const roles = parseAihrRoles(auth0.user?.[AUTH0_ROLES_CLAIM]);
  const user: AihrUser | undefined = auth0.user
    ? {
        name: auth0.user.name,
        email: auth0.user.email,
        picture: auth0.user.picture,
        roles
      }
    : undefined;

  const value: AihrAuthContextValue = {
    authEnabled: true,
    isLoading: auth0.isLoading,
    isAuthenticated: auth0.isAuthenticated,
    error: auth0.error,
    user,
    hasCosmosAccess: hasCosmosAccess(roles),
    login: () =>
      auth0.loginWithRedirect({
        authorizationParams: {
          audience: AUTH0_AUDIENCE,
          scope: AUTH0_SCOPE,
          connection: "google-oauth2"
        },
        appState: {
          returnTo: window.location.pathname + window.location.search
        }
      }),
    logout: () =>
      auth0.logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      }),
    getAccessToken: () =>
      auth0.getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH0_AUDIENCE
        }
      })
  };

  return (
    <AihrAuthContext.Provider value={value}>
      {children}
    </AihrAuthContext.Provider>
  );
}

export function useAihrAuth() {
  return useContext(AihrAuthContext);
}
